import React, { useState, useEffect, useContext, useRef } from 'react';
import { Modal } from '../layout/Modal';
import MatchGroupContext from '../../context/matchGroups/matchGroupContext';
import UnifiedMatchContext from '../../context/matches/UnifiedMatchContext';
import { format } from 'date-fns';
import './MatchGroupModal.css';

const MatchGroupModal = ({ isOpen, onClose, defaultType = 'tcg', initialGroupId = null }) => {
  const matchGroupContext = useContext(MatchGroupContext);
  const {
    matchGroups,
    getMatchGroups,
    createMatchGroup,
    addMatchesToGroup
  } = matchGroupContext;

  const matchContext = useContext(UnifiedMatchContext);
  const {
    tcgMatches,
    vgcMatches,
    getTCGMatches,
    getVGCMatches
  } = matchContext;

  const [activeTab, setActiveTab] = useState(defaultType);
  const [selectedMatches, setSelectedMatches] = useState({ tcg: [], vgc: [] });
  const [search, setSearch] = useState('');
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    type: defaultType,
    isNewGroup: !initialGroupId,
    selectedGroupId: initialGroupId || ''
  });

  // Use refs to track if we've already fetched data
  const didFetchGroupsRef = useRef(false);
  const didFetchTcgRef = useRef(false);
  const didFetchVgcRef = useRef(false);
  
  // Load match groups and matches when the component mounts or when modal opens
  useEffect(() => {
    if (isOpen) {
      // Only fetch groups if they haven't been fetched yet
      if (!didFetchGroupsRef.current && matchGroups.length === 0) {
        getMatchGroups();
        didFetchGroupsRef.current = true;
      }
      
      // Only fetch matches if they haven't been fetched yet
      if (activeTab === 'tcg' && !didFetchTcgRef.current && tcgMatches.length === 0) {
        getTCGMatches();
        didFetchTcgRef.current = true;
      } else if (activeTab === 'vgc' && !didFetchVgcRef.current && vgcMatches.length === 0) {
        getVGCMatches();
        didFetchVgcRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load matches when the tab changes, only if modal is open
  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'tcg' && !didFetchTcgRef.current && tcgMatches.length === 0) {
        getTCGMatches();
        didFetchTcgRef.current = true;
      } else if (activeTab === 'vgc' && !didFetchVgcRef.current && vgcMatches.length === 0) {
        getVGCMatches();
        didFetchVgcRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isOpen]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFormState(prev => ({ ...prev, type: tab }));
  };

  const handleMatchSelect = (matchId, matchType) => {
    setSelectedMatches(prev => {
      const matchArray = prev[matchType];
      
      if (matchArray.includes(matchId)) {
        return {
          ...prev,
          [matchType]: matchArray.filter(id => id !== matchId)
        };
      } else {
        return {
          ...prev,
          [matchType]: [...matchArray, matchId]
        };
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleGroupTypeChange = (e) => {
    const isNewGroup = e.target.value === 'new';
    setFormState(prev => ({
      ...prev,
      isNewGroup,
      selectedGroupId: isNewGroup ? '' : e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, description, type, isNewGroup, selectedGroupId } = formState;

    if (isNewGroup) {
      if (!name.trim()) {
        alert('Please enter a group name');
        return;
      }

      // Create a new group
      const matches = {
        tcg: selectedMatches.tcg,
        vgc: selectedMatches.vgc
      };

      const result = await createMatchGroup({
        name,
        description,
        type: type === 'both' ? 'mixed' : type,
        matches
      });

      if (result.success) {
        onClose();
      }
    } else {
      // Add matches to existing group
      if (!selectedGroupId) {
        alert('Please select a group');
        return;
      }

      const matches = {
        tcgMatches: selectedMatches.tcg,
        vgcMatches: selectedMatches.vgc
      };

      const result = await addMatchesToGroup(selectedGroupId, matches);
      
      if (result.success) {
        onClose();
      }
    }
  };

  // Filter matches based on search term
  const filterMatches = (matches) => {
    if (!search) return matches;
    
    return matches.filter(match => {
      if (activeTab === 'tcg') {
        return (
          match.opponentDeck.toLowerCase().includes(search.toLowerCase()) ||
          (match.activeDeck[0] && match.activeDeck[0].toLowerCase().includes(search.toLowerCase()))
        );
      } else {
        return (
          (match.playerTeam && match.playerTeam.toLowerCase().includes(search.toLowerCase())) ||
          (match.opponentTeam && match.opponentTeam.toLowerCase().includes(search.toLowerCase())) ||
          (match.opponentName && match.opponentName.toLowerCase().includes(search.toLowerCase()))
        );
      }
    });
  };

  const renderMatchList = () => {
    const matches = activeTab === 'tcg' ? filterMatches(tcgMatches) : filterMatches(vgcMatches);
    
    if (matches.length === 0) {
      return <div className="no-matches">No matches found</div>;
    }

    return matches.map(match => {
      const isSelected = selectedMatches[activeTab].includes(match._id);
      const matchDate = new Date(match.createdAt || match.date);
      
      let matchTitle = '';
      let result = '';
      
      if (activeTab === 'tcg') {
        matchTitle = `${match.activeDeck[0]} vs ${match.opponentDeck}`;
        
        // Calculate the result from games
        if (match.games && match.games.length > 0) {
          const wins = match.games.filter(game => game.result === 'win').length;
          const losses = match.games.filter(game => game.result === 'loss').length;
          result = wins > losses ? 'win' : losses > wins ? 'loss' : 'tie';
        }
      } else {
        matchTitle = `${match.playerTeam || 'My Team'} vs ${match.opponentTeam || match.opponentName || 'Opponent'}`;
        result = match.result;
      }
      
      return (
        <div
          key={match._id}
          className={`match-list-item ${isSelected ? 'selected' : ''}`}
          onClick={() => handleMatchSelect(match._id, activeTab)}
        >
          <div className="match-info">
            <div>{matchTitle}</div>
            <div className="match-date">{format(matchDate, 'MMM d, yyyy')}</div>
          </div>
          <div>
            <span className={`badge badge-${result}`}>
              {result === 'win' ? 'W' : result === 'loss' ? 'L' : 'T'}
            </span>
          </div>
        </div>
      );
    });
  };

  const groupOptions = matchGroups
    .filter(group => group.type === activeTab || group.type === 'mixed')
    .map(group => (
      <option key={group._id} value={group._id}>
        {group.name}
      </option>
    ));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Match Group" customClass="match-group-modal">
      <div className="tab-buttons">
        <button
          className={`tab-button ${activeTab === 'tcg' ? 'active' : ''}`}
          onClick={() => handleTabChange('tcg')}
        >
          TCG Matches
        </button>
        <button
          className={`tab-button ${activeTab === 'vgc' ? 'active' : ''}`}
          onClick={() => handleTabChange('vgc')}
        >
          VGC Matches
        </button>
      </div>

      <form className="match-group-form" onSubmit={handleSubmit}>
        <div>
          <label>
            <input
              type="radio"
              name="groupType"
              value="existing"
              checked={!formState.isNewGroup}
              onChange={handleGroupTypeChange}
            />
            Add to existing group
          </label>
          {!formState.isNewGroup && (
            <select
              className="form-control group-select"
              value={formState.selectedGroupId}
              onChange={(e) => setFormState(prev => ({ ...prev, selectedGroupId: e.target.value }))}
            >
              <option value="">Select a group</option>
              {groupOptions}
            </select>
          )}
        </div>

        <div>
          <label>
            <input
              type="radio"
              name="groupType"
              value="new"
              checked={formState.isNewGroup}
              onChange={handleGroupTypeChange}
            />
            Create new group
          </label>
          {formState.isNewGroup && (
            <div className="create-new-group">
              <input
                type="text"
                name="name"
                className="form-control"
                placeholder="Group Name"
                value={formState.name}
                onChange={handleInputChange}
              />
              <textarea
                name="description"
                className="form-control"
                placeholder="Group Description (optional)"
                value={formState.description}
                onChange={handleInputChange}
              />
            </div>
          )}
        </div>

        <div className="section-title">Select Matches</div>
        <input
          type="text"
          className="form-control search-box"
          placeholder="Search matches..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="match-list">
          {renderMatchList()}
        </div>

        <div className="selected-count">
          Selected: {selectedMatches[activeTab].length} matches
        </div>

        <div className="form-buttons">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={(formState.isNewGroup && !formState.name) || 
                    (!formState.isNewGroup && !formState.selectedGroupId) ||
                    (selectedMatches.tcg.length === 0 && selectedMatches.vgc.length === 0)}
          >
            {formState.isNewGroup ? 'Create Group' : 'Add to Group'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default MatchGroupModal;